import { FC } from "react"

import { ReactComponent as IconRoundPlus } from "@common_assets/svg/roundplus-outlined.svg"
import {
  Box,
  Divider,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material"
import IconWrapper from "components/common/icon/iconWrapper"
import { useTranslation } from "react-i18next"
import { ExportTask, removeExportTask } from "utils/exportTasks"

import ExportFileRow from "./ExportFileRow"

interface IInlineExportSubmenuProps {
  open: boolean
  successTasks: ExportTask[]
  onGenerateNewFile: () => void
  onFileClick: (taskId: number) => void
  isGenerateDisabled?: boolean
  generateDisabledTooltip?: string
  onFileRemove?: (taskId: number) => void
  wrapFilename?: boolean
}

/**
 * Inline accordion-style export submenu used inside popover menus (e.g. StudentProfile, Jobs).
 * Unlike ExportMenu (which renders its own Popover), this renders inline content
 * that expands/collapses within a parent popover.
 */
const InlineExportSubmenu: FC<IInlineExportSubmenuProps> = ({
  open,
  successTasks,
  onGenerateNewFile,
  onFileClick,
  isGenerateDisabled = false,
  generateDisabledTooltip = "",
  onFileRemove,
  wrapFilename = false,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  if (!open) return null

  return (
    <li>
      <Box
        sx={{
          backgroundColor: theme.palette.mockup.primary95,
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          my: 0.5,
          ...(wrapFilename && { maxWidth: "320px" }),
        }}
      >
        <Tooltip
          title={isGenerateDisabled ? generateDisabledTooltip : ""}
          arrow
          placement="top"
        >
          <Box
            role="button"
            tabIndex={0}
            aria-label={t("allTransactions.generateNewFile")}
            onClick={isGenerateDisabled ? undefined : onGenerateNewFile}
            onKeyDown={(event) => {
              if (
                !isGenerateDisabled &&
                (event.key === "Enter" || event.key === " ")
              ) {
                event.preventDefault()
                onGenerateNewFile()
              }
            }}
            aria-disabled={isGenerateDisabled}
            sx={{
              py: 1.5,
              px: 2,
              cursor: isGenerateDisabled ? "default" : "pointer",
              opacity: isGenerateDisabled ? 0.5 : 1,
              "&:hover": {
                backgroundColor: isGenerateDisabled
                  ? "transparent"
                  : theme.palette.action.hover,
              },
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: -2,
              },
            }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <IconWrapper
                sx={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconRoundPlus
                  style={{
                    width: "20px",
                    height: "20px",
                    color: isGenerateDisabled
                      ? theme.palette.text.disabled
                      : theme.palette.primary.main,
                  }}
                />
              </IconWrapper>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: theme.typography.fontWeightRegular,
                  color: isGenerateDisabled
                    ? theme.palette.text.disabled
                    : "inherit",
                }}
              >
                {t("allTransactions.generateNewFile")}
              </Typography>
            </Stack>
          </Box>
        </Tooltip>
        <Divider />
        {successTasks.length > 0 ? (
          successTasks.map((task) => (
            <ExportFileRow
              key={task.id}
              task={task}
              onClick={() => onFileClick(task.id)}
              onRemove={() =>
                onFileRemove ? onFileRemove(task.id) : removeExportTask(task.id)
              }
              px={2}
              outlineOffset={-2}
            />
          ))
        ) : (
          <Box
            sx={{
              py: 1.5,
              px: 2,
              opacity: 0.5,
            }}
          >
            <Typography variant="body2">
              {t("allTransactions.noTransactionRecords")}
            </Typography>
          </Box>
        )}
      </Box>
    </li>
  )
}

export default InlineExportSubmenu
